import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import Pagination from '../components/Pagination.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { TX_TYPE_FILTERS } from '../data/constants'
import { fetchTransactionsPage } from '../services/inventoryApi'
import { formatDateTime } from '../utils/format'
import { formatSignedQty, partTitle, signedTransactionQty } from '../utils/stock'

const PAGE_SIZES = [20, 50, 100]

function parsePage(value) {
  const n = Number.parseInt(value, 10)
  return Number.isInteger(n) && n > 0 ? n : 1
}

function parseLimit(value) {
  const n = Number.parseInt(value, 10)
  return PAGE_SIZES.includes(n) ? n : 20
}

export default function TransactionsPage() {
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') || '')
  const [type, setType] = useState(params.get('type') || 'all')
  const [page, setPage] = useState(parsePage(params.get('page')))
  const [limit, setLimit] = useState(parseLimit(params.get('limit')))
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState('')

  function syncParams(next) {
    const merged = { q: query, type, page, limit, ...next }
    const search = new URLSearchParams()
    if (merged.q) search.set('q', merged.q)
    if (merged.type && merged.type !== 'all') search.set('type', merged.type)
    if (Number(merged.page) > 1) search.set('page', String(merged.page))
    if (Number(merged.limit) && Number(merged.limit) !== 20) search.set('limit', String(merged.limit))
    setParams(search, { replace: true })
  }

  function changeFilters(next) {
    if (next.q !== undefined) setQuery(next.q)
    if (next.type !== undefined) setType(next.type)
    setPage(1)
    syncParams({ ...next, page: 1 })
  }

  function changePage(nextPage) {
    setPage(nextPage)
    syncParams({ page: nextPage })
  }

  function changeLimit(nextLimit) {
    setLimit(nextLimit)
    setPage(1)
    syncParams({ limit: nextLimit, page: 1 })
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setListLoading(true)
      setListError('')
      try {
        const result = await fetchTransactionsPage({
          q: query,
          type,
          page,
          limit,
        })
        if (cancelled) return
        setItems(result.items)
        setTotal(result.total)
        setTotalPages(result.totalPages)
        if (result.page !== page) {
          setPage(result.page)
          syncParams({ page: result.page })
        }
      } catch (err) {
        if (!cancelled) setListError(err.message || '이력을 불러오지 못했습니다.')
      } finally {
        if (!cancelled) setListLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [query, type, page, limit])

  return (
    <div className="page">
      <PageHeader title="입출고 이력" description="입고·출고·초기재고·재고조정 기록을 검색하고 확인합니다." />

      {listError ? <p className="form-error">{listError}</p> : null}

      <section className="filter-panel">
        <div className="search-row">
          <input
            className="input search-input"
            value={query}
            onChange={(e) => changeFilters({ q: e.target.value })}
            placeholder="모델명, 제조사, 관리번호, 메모 검색"
            aria-label="입출고 검색"
          />
        </div>
        <div className="filter-toolbar">
          <div className="filter-group">
            <span className="filter-group__label">거래 유형</span>
            <div className="segmented" role="group" aria-label="거래 유형">
              {TX_TYPE_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`segmented__btn ${type === item.value ? 'is-active' : ''}`}
                  onClick={() => changeFilters({ type: item.value })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <p className="result-count">
        {listLoading ? '불러오는 중...' : `총 ${total.toLocaleString('ko-KR')}건`}
      </p>

      {items.length === 0 ? (
        <p className="empty">{listLoading ? '이력을 불러오는 중입니다.' : '조건에 맞는 이력이 없습니다.'}</p>
      ) : (
        <>
          <div className="card-list">
            {items.map((tx) => {
              const delta = signedTransactionQty(tx)
              return (
                <Link key={tx.id} to={`/inventory/${tx.partId}`} className="part-card">
                  <div className="part-card__top">
                    <span className="muted">{formatDateTime(tx.createdAt)}</span>
                    <StatusBadge kind="tx" status={tx.type} />
                  </div>
                  <strong className="model-name">{partTitle(tx)}</strong>
                  <span>
                    {tx.partCode} · {tx.manufacturer}
                  </span>
                  <p className="part-card__spec">{tx.memo || tx.worker || '-'}</p>
                  <div className="part-card__qty">
                    <b className={delta < 0 ? 'tx-delta--minus' : 'tx-delta--plus'}>
                      {formatSignedQty(delta)}
                    </b>
                    <em>잔여 {tx.afterQuantity ?? tx.balanceAfter}</em>
                  </div>
                </Link>
              )
            })}
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>구분</th>
                  <th>관리번호</th>
                  <th>제조사</th>
                  <th>모델명</th>
                  <th>수량</th>
                  <th>잔여</th>
                  <th>작업자</th>
                  <th>메모</th>
                </tr>
              </thead>
              <tbody>
                {items.map((tx) => {
                  const delta = signedTransactionQty(tx)
                  return (
                    <tr key={tx.id}>
                      <td>{formatDateTime(tx.createdAt)}</td>
                      <td>
                        <StatusBadge kind="tx" status={tx.type} />
                      </td>
                      <td>{tx.partCode}</td>
                      <td>{tx.manufacturer}</td>
                      <td>
                        <Link to={`/inventory/${tx.partId}`} className="model-link model-name">
                          {partTitle(tx)}
                        </Link>
                      </td>
                      <td className={`td-qty ${delta < 0 ? 'tx-delta--minus' : 'tx-delta--plus'}`}>
                        {formatSignedQty(delta)}
                      </td>
                      <td>{tx.afterQuantity ?? tx.balanceAfter}</td>
                      <td>{tx.worker || '-'}</td>
                      <td>{tx.note || tx.memo || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        disabled={listLoading}
        itemLabel="건"
        limitLabel="페이지당 거래 수"
        onPageChange={changePage}
        onLimitChange={changeLimit}
      />
    </div>
  )
}
